import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Stethoscope, Sparkles, PhoneCall, ArrowRight, Loader2, RefreshCw, Wand2, Lock, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { useSession } from "../context/SessionContext";
import { getSession, photoUrl, startAssessment } from "../lib/api";
import { DiagnoseReport, ImproveReport } from "../components/AssessmentReport";
import ConceptFlow from "../components/ConceptFlow";
import ProjectLink from "../components/ProjectLink";
import VerdantCheck from "../components/VerdantCheck";
import ReportOffer from "../components/ReportOffer";
import DiagnosticPaywall from "../components/DiagnosticPaywall";
import { useConfig } from "../lib/useConfig";

function HeroPhoto({ photo, session, isDiagnose, analyzing }) {
  const HeaderIcon = isDiagnose ? Stethoscope : Sparkles;
  if (!photo) return null;
  return (
    <section className="relative rounded-3xl overflow-hidden bg-[#092C15]">
      <img data-testid="pathway-hero-photo" src={photoUrl(photo.id)} alt="Your landscape today" className="w-full aspect-[16/10] object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      {analyzing && <div className="gf-scan-line" />}
      <div className="absolute top-3 left-3 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur text-white text-[11px] font-semibold px-2.5 py-1">
        <HeaderIcon size={14} /> {isDiagnose ? "DIAGNOSE MY LANDSCAPE" : "YOUR LANDSCAPE TODAY"}
      </div>
      <div className="absolute bottom-3 left-3 right-3 text-white">
        <div className="font-display text-base font-semibold truncate">{session.customer.name}</div>
        <div className="text-xs text-emerald-50/80 truncate">
          {[session.customer.address, session.customer.city, session.customer.zip].filter(Boolean).join(", ")}
        </div>
      </div>
    </section>
  );
}

function Analyzing({ isDiagnose }) {
  return (
    <div data-testid="assessment-loading" className="rounded-2xl bg-white border border-slate-200 p-6 text-center">
      <Loader2 className={`mx-auto animate-spin ${isDiagnose ? "text-sky-600" : "text-emerald-600"}`} size={30} />
      <div className="mt-3 font-display font-bold text-[#092C15]">
        {isDiagnose ? "Dr. Verdant™ is reviewing your photos…" : "GreenFlow is exploring your landscape's potential…"}
      </div>
      <div className="mt-1 text-sm text-slate-500">This usually takes 30–60 seconds. Please keep this screen open.</div>
    </div>
  );
}

function ConceptPlaceholder() {
  return (
    <section data-testid="vision-placeholder" className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl grid place-items-center bg-emerald-100 text-emerald-700"><ImageIcon size={18} /></div>
        <h2 className="font-display text-lg font-bold text-[#092C15]">See Your GreenFlow Landscape Vision</h2>
      </div>
      <p className="mt-2 text-sm text-slate-600">A concept rendering of your enhanced property is coming soon.</p>
      <Button data-testid="generate-concept-button" disabled className="mt-4 w-full h-12 rounded-xl font-semibold tracking-wide bg-emerald-700 text-white">
        <Lock size={16} className="mr-2" /> GENERATE MY LANDSCAPE CONCEPT
      </Button>
    </section>
  );
}

export default function PathwayScreen({ mode }) {
  const navigate = useNavigate();
  const { session, setSession } = useSession();
  const [retrying, setRetrying] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const cfg = useConfig();
  const status = session?.assessment_status;

  useEffect(() => {
    if (!session || paywall || status === "complete" || status === "error") return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const fresh = await getSession(session.id);
        if (!alive) return;
        if (!fresh.assessment_status) {
          await startAssessment(session.id);
          return;
        }
        if (fresh.assessment_status !== "processing") setSession(fresh);
      } catch (ex) {
        const code = ex?.response?.status;
        if (!alive) return;
        if (code === 402) setPaywall(ex.response.data.detail);
        else if (code >= 400 && code < 500) setSession({ ...session, assessment_status: "error", assessment_error: ex.response.data?.detail });
      }
    };
    tick();
    const t = setInterval(tick, 3500);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [session?.id, status, paywall]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return <Navigate to="/" replace />;

  const isDiagnose = mode === "diagnose";
  const photos = session.photos || [];
  const hero = photos.find((p) => p.is_primary) || photos[0];
  const extras = photos.filter((p) => p !== hero);
  const v3 = !isDiagnose && !!cfg?.phase3_enabled && status === "complete" && session.assessment?.version === "phase3";
  const conceptDone = v3 && session.concept?.status === "complete";

  const retry = async () => {
    setRetrying(true);
    try {
      await startAssessment(session.id);
      setSession({ ...session, assessment_status: "processing", assessment_error: null });
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not restart the assessment.");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]">
      <BrandHeader
        back
        title={isDiagnose ? "Preliminary Assessment" : "Enhancement Assessment"}
        subtitle={isDiagnose ? "Diagnose My Landscape" : "Improve My Landscape"}
      />
      <main className={`mx-auto ${conceptDone ? "max-w-4xl" : "max-w-xl"} px-5 py-6 gf-fade-in space-y-4`}>
        {conceptDone && (
          <ConceptFlow session={session} setSession={setSession} cfg={cfg}>
            <ProjectLink token={session.access_token} />
            <ImproveReport a={session.assessment} />
          </ConceptFlow>
        )}
        {!conceptDone && <HeroPhoto photo={hero} session={session} isDiagnose={isDiagnose} analyzing={status !== "complete" && !paywall} />}
        {!conceptDone && extras.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {extras.map((p) => (
              <img key={p.id} src={photoUrl(p.id)} alt={p.category || "detail"} className="h-16 w-16 rounded-lg object-cover border border-slate-200 shrink-0" />
            ))}
          </div>
        )}

        {conceptDone ? null : status === "complete" && session.assessment ? (
          isDiagnose ? <DiagnoseReport a={session.assessment} /> : <ImproveReport a={session.assessment} />
        ) : status === "error" ? (
          <div data-testid="assessment-error" className="rounded-2xl bg-white border border-rose-200 p-5 text-center">
            <div className="font-semibold text-rose-700">We couldn't complete the assessment.</div>
            <div className="text-sm text-slate-500 mt-1">{typeof session.assessment_error === "string" && session.assessment_error.startsWith("Too many") ? session.assessment_error : "Your photos are saved. Please try again."}</div>
            <Button data-testid="assessment-retry-button" onClick={retry} disabled={retrying} variant="outline" className="mt-4 rounded-full">
              <RefreshCw size={14} className="mr-2" /> Try again
            </Button>
          </div>
        ) : paywall ? (
          <DiagnosticPaywall sessionId={session.id} info={paywall} />
        ) : (
          <Analyzing isDiagnose={isDiagnose} />
        )}

        {!isDiagnose && status === "complete" && !v3 && <ConceptPlaceholder />}
        {isDiagnose && status === "complete" && session.assessment && cfg?.verdant_enabled && (
          <VerdantCheck session={session} setSession={setSession} ackText={cfg.verdant_ack} />
        )}
        {isDiagnose && status === "complete" && session.assessment && cfg?.verdant_enabled && <ReportOffer sessionId={session.id} />}
        {v3 && !conceptDone && <ConceptFlow session={session} setSession={setSession} cfg={cfg} />}
        {status === "complete" && !conceptDone && <ProjectLink token={session.access_token} />}

        {!conceptDone && (
        <section data-testid="panel-consultation-cta" className="rounded-3xl p-5 text-white gf-gradient-dark relative overflow-hidden">
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-300 flex items-center gap-1.5">
            <Wand2 size={12} /> GreenFlow Landcare &amp; Irrigation
          </div>
          <h3 data-testid="cta-heading" className="font-display text-2xl font-bold mt-1">Ready for GreenFlow to Take a Closer Look?</h3>
          <p className="mt-1 text-emerald-100/80 text-sm">A GreenFlow specialist will review your photos and assessment, then reach out to plan next steps.</p>
          <Button
            data-testid="go-to-vision-button"
            onClick={() => navigate("/vision")}
            className="mt-4 w-full min-h-12 h-auto py-3 whitespace-normal text-sm sm:text-base bg-white text-[#092C15] hover:bg-emerald-50 font-bold tracking-wide rounded-xl"
          >
            <PhoneCall className="mr-2" size={18} /> REQUEST A GREENFLOW CONSULTATION <ArrowRight className="ml-2" size={18} />
          </Button>
        </section>
        )}
      </main>
    </div>
  );
}
